import React, { useCallback, useEffect, useState } from 'react'
import { CAlert, CBadge, CButton, CSmartTable } from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilHeart, cilReload, cilWarning } from '@coreui/icons'
import moment from 'moment'
import CommonService from 'src/service/CommonService'
import { InfoBox } from 'src/components/common/ImportWizard'

// The endpoint puts the sort key straight into ORDER BY across a players/scorecards/fixtures
// join, so name each sortable column by its table to keep it unambiguous.
const SORT_COLUMNS = {
  full_name: 'players.full_name',
  fantasy_points: 'fixture_scorecards.fantasy_points',
  rs: 'fixture_scorecards.rs',
  wks: 'fixture_scorecards.wks',
}

const initials = (name = '') =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('')

const PlayerAvatar = ({ name, image }) => {
  const [failed, setFailed] = useState(false)
  if (image && !failed) {
    return (
      <img
        className="pl-avatar"
        src={`${process.env.REACT_APP_API_URL}uploads/player/${image}`}
        alt=""
        onError={() => setFailed(true)}
      />
    )
  }
  return <span className="pl-avatar pl-avatar--initials">{initials(name) || '?'}</span>
}

const Table = ({ type, threshold, refreshKey, onTotalChange }) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [activePage, setActivePage] = useState(1)
  // Best performances first: sort on the list's own stat until the admin picks another column.
  const [columnSorter, setColumnSorter] = useState({ column: type.stat, state: 'desc' })
  const [itemsPerPage, setItemsPerPage] = useState(20)
  const [rows, setRows] = useState([])

  const columns = [
    { key: 'full_name', label: 'Player', filter: false },
    { key: 'match', label: 'Match', filter: false, sorter: false },
    { key: type.stat, label: type.statLabel, filter: false, _style: { width: '18%' } },
    { key: 'total_like', label: 'Likes', filter: false, sorter: false, _style: { width: '8%' } },
    {
      key: 'match_date',
      label: 'Match Date',
      filter: false,
      sorter: false,
      _style: { width: '14%' },
    },
  ]

  const fetchRows = useCallback(() => {
    setLoading(true)
    setError(false)
    const offset = itemsPerPage * activePage - itemsPerPage
    let params = new URLSearchParams()
    columnSorter &&
      columnSorter.column !== undefined &&
      params.append(
        'sort',
        `${SORT_COLUMNS[columnSorter.column] || columnSorter.column}%${columnSorter.state}`,
      )

    CommonService.getTimelineData(type.key, offset, itemsPerPage, activePage, params)
      .then((result) => {
        setRows(result.data)
        onTotalChange?.(type.key, result.data?.total ?? 0)
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false))
    // refreshKey is a dependency on purpose: saving new thresholds changes what qualifies.
  }, [type.key, activePage, columnSorter, itemsPerPage, onTotalChange, refreshKey]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchRows()
  }, [fetchRows])

  const noItemsLabel = threshold
    ? `No innings with ${threshold}+ ${type.unit} yet`
    : `No ${type.label.toLowerCase()} innings yet`

  return (
    <>
      <InfoBox>
        {threshold ? (
          <>
            Every innings with <strong>{threshold}+</strong> {type.unit} in this game&apos;s
            matches.
          </>
        ) : (
          <>Every innings that reaches the {type.label.toLowerCase()} threshold.</>
        )}{' '}
        Likes are what members gave the post in the app.
      </InfoBox>

      {error ? (
        <CAlert color="danger" className="d-flex flex-wrap align-items-center gap-2 mb-0">
          <CIcon icon={cilWarning} className="flex-shrink-0" />
          <span>We couldn&apos;t load the {type.label.toLowerCase()} Pulse.</span>
          <CButton
            color="danger"
            variant="outline"
            size="sm"
            className="ms-auto"
            onClick={fetchRows}
          >
            <CIcon icon={cilReload} className="me-1" />
            Retry
          </CButton>
        </CAlert>
      ) : (
        <div className="ge-datatable ge-datatable--no-select ge-datatable--static">
          <CSmartTable
            columns={columns}
            columnSorter={{ external: true }}
            sorterValue={columnSorter}
            scopedColumns={{
              full_name: (item) => (
                <td>
                  <div className="pl-player">
                    <PlayerAvatar name={item.full_name} image={item.player_image} />
                    <div style={{ minWidth: 0 }}>
                      <div className="pl-player-name">{item.full_name || 'Player removed'}</div>
                      {item.game_type && <div className="pl-meta">{item.game_type}</div>}
                    </div>
                  </div>
                </td>
              ),
              match: (item) => (
                <td>
                  <div className="pl-match">
                    {item.team_name || 'TBC'}
                    {item.team2_name && <span className="pl-meta"> vs </span>}
                    {item.team2_name}
                  </div>
                  <div className="pl-meta">
                    {[item.round && `Round ${item.round}`, item.match_type_name]
                      .filter(Boolean)
                      .join(' · ')}
                  </div>
                </td>
              ),
              [type.stat]: (item) => (
                <td>
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    <span className="pl-stat">{item[type.stat] ?? 0}</span>
                    {item[type.tagKey] && (
                      <CBadge shape="rounded-pill" color={type.tone}>
                        {item[type.tagKey]}
                      </CBadge>
                    )}
                  </div>
                </td>
              ),
              total_like: (item) => (
                <td>
                  <span className="pl-likes">
                    <CIcon icon={cilHeart} size="sm" />
                    {item.total_like ?? 0}
                  </span>
                </td>
              ),
              match_date: (item) => {
                const date = item.fixture_start_date || item.created_at
                return <td className="pl-meta">{date ? moment(date).format('D MMM YYYY') : '-'}</td>
              },
            }}
            items={rows?.data}
            itemsPerPage={itemsPerPage}
            itemsPerPageSelect
            loading={loading}
            noItemsLabel={noItemsLabel}
            pagination={{ external: true }}
            paginationProps={{
              activePage: activePage,
              pages: Math.ceil(rows?.total / itemsPerPage) || 1,
            }}
            tableProps={{ hover: true, responsive: true, className: 'align-middle mb-0' }}
            onActivePageChange={(activePage) => setActivePage(activePage)}
            onItemsPerPageChange={(itemsPerPage) => {
              setActivePage(1)
              setItemsPerPage(itemsPerPage)
            }}
            onSorterChange={(sorter) => {
              setActivePage(1)
              setColumnSorter(sorter)
            }}
          />
        </div>
      )}
    </>
  )
}

export default Table
